import React, { useEffect, useState } from "react";
import { Layout, Input, Radio, Form, Button, Typography } from "antd";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  saveAssesmentResponse,
  getTraningAssesmentQuestions,
  getAssesmentQuestions,
  saveTrainingAssesmentResponse,
} from "../services/ApiActions/lmsAct";
import Loader from "../components/Layout/Loader";
import PageHeading from "../components/Layout/PageHeading";
import { Content } from "antd/es/layout/layout";
import LMSBreadcrumb from "../components/LMSComponent/LMSBreadcrumb";
import Toast from "../services/ToastMessage";

const { TextArea } = Input;
const { Text, Title } = Typography;

// Types
interface Option {
  option: string;
  id: number;
}

interface Question {
  id: number;
  question: string;
  options: Option[];
  is_multiple_choice_question: boolean;
  submitted_answer?: string;
  is_correct?: boolean;
  correct_answer?: number;
  total_marks: number;
}

interface AssessmentDetails {
  id: string;
  name: string;
  questions: Question[];
  is_attempted: boolean;
  is_submitted: boolean;
  assessment_result?: "pass" | "fail";
  reattempt_enabled?: boolean;
  assessment_questions?: any;
  obtained_marks: number;
  total_marks: number;
  percentage: number;
}

interface FormResponse {
  submitted_answer: string;
  question_id: number;
}

interface SubmitAssessmentData {
  responses: FormResponse[];
  assessment_id?: string;
  course_id?: number;
  id?: string;
}

interface AssessmentPageProps {
  isTrainingAssessment?: boolean;
}

const AssessmentPage: React.FC<AssessmentPageProps> = ({
  isTrainingAssessment = false,
}) => {
  // Hooks
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, assesmentId } = useParams<{
    courseId?: string;
    assesmentId: string;
  }>();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [courseName, setCourseName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [assessmentDetails, setAssessmentDetails] =
    useState<AssessmentDetails | null>(null);

  // URL and parameters
  const params = new URLSearchParams(location.search);
  const isReattempt = params.get("reattempt") === "true";
  const backURL = isTrainingAssessment
    ? `/lms/training-assessment/${assesmentId}`
    : `/lms/course/${courseId}`;

  // Computed properties
  const assessmentResult =
    assessmentDetails?.assessment_result?.toLocaleLowerCase();
  const isDeclaredFail = assessmentResult === "fail";

  const isDisabled = assessmentDetails
    ? isTrainingAssessment
      ? assessmentDetails.is_attempted &&
        !(isReattempt && assessmentDetails.reattempt_enabled)
      : assessmentDetails?.is_attempted && !(isDeclaredFail && isReattempt)
    : false;

  const assessmentQuestions = assessmentDetails
    ? isTrainingAssessment
      ? assessmentDetails.questions
      : assessmentDetails.assessment_questions
    : [];
  const questions = [...assessmentQuestions].sort(() => Math.random() - 0.5);
  // API Functions
  const fetchAssessmentData = async () => {
    if (!isTrainingAssessment && !courseId) {
      setErrorMessage("Course ID is missing");
      setIsLoading(false);
      return;
    }
    if (!assesmentId) {
      setErrorMessage("Assessment ID is missing");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const response: any = isTrainingAssessment
        ? await getTraningAssesmentQuestions(assesmentId)
        : courseId && (await getAssesmentQuestions(assesmentId, courseId));

      if (response?.data) {
        console.log('response.data ---', response.data);
        setAssessmentDetails(response.data);
        setCourseName(sessionStorage.getItem("course_name") || "");
      } else {
        setErrorMessage("No assessment data available");
      }
    } catch (error) {
      console.error("Error fetching assessment questions:", error);
      setErrorMessage(
        "Unable to get assessment details. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const submitAssessment = async (data: SubmitAssessmentData) => {
    try {
      setIsProcessing(true);

      const response: any = isTrainingAssessment
        ? await saveTrainingAssesmentResponse(data)
        : await saveAssesmentResponse(data);

      if(response.transaction_status === 'error'){
        Toast({
          type: 'error',
          message: response?.message || 'Something went wrong.',
        });
        return;
      }
      setAssessmentDetails({
        ...response.data,
        is_submitted: true,
      });
    } catch (error) {
      console.error("Error submitting assessment:", error);
      setErrorMessage("Failed to submit assessment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Event handlers
  const handleSubmit = (values: Record<string, number>) => {
    if (!assessmentDetails) return;

    const formResponses = Object.entries(values).map(([key, value]) => ({
      submitted_answer: String(value),
      question_id: Number(key.replace("question-", "")),
    }));

    const payload = isTrainingAssessment
      ? { responses: formResponses, id: assessmentDetails.id }
      : {
          responses: formResponses,
          assessment_id: assessmentDetails.id || assesmentId,
          course_id: Number(courseId),
          ...(isReattempt && { is_reattempt: true }),
        };
    submitAssessment(payload);
  };

  // Helper functions
  const getAnswerStyleInfo = (
    isCorrect?: boolean,
    optionIndex: number = 0,
    submittedOption: number = 0
  ) => {
    if (
      typeof isCorrect === "undefined" ||
      optionIndex !== submittedOption - 1
    ) {
      return null;
    }

    return isCorrect
      ? { className: "correct-answer", text: "(Correct Answer)" }
      : { className: "incorrect-answer", text: "(Wrong Answer)" };
  };

  // Effects
  useEffect(() => {
    fetchAssessmentData();
  }, [assesmentId]);

  // Render components
  const renderResultStatus = () => {
    <LMSBreadcrumb courseName={assessmentDetails?.name || ""} />;
    if (!assessmentDetails?.assessment_result) return null;
    const result = assessmentDetails.assessment_result.toLocaleLowerCase();
    if (!(result === "fail" || result === "pass") || isReattempt) return null;
    return (
      <>
        <Text
          style={{
            borderRadius: "4px",
            padding: "5px 25px",
            color: "#fff",
            backgroundColor: result === "pass" ? "#52C41A" : "#FF4D4F",
            marginRight: "10px",
          }}
        >
          {result === "pass" ? "Pass" : "Fail"}
        </Text>
      </>
    );
  };

  const renderQuestionOptions = (question: Question, index: number) => {
    const {
      id,
      is_correct,
      correct_answer,
      options,
      submitted_answer,
      is_multiple_choice_question,
    } = question;

    const answerIndex = correct_answer ? correct_answer - 1 : -1;
    const submittedAnswerNum =
      submitted_answer &&
      !(isTrainingAssessment
        ? isReattempt && assessmentDetails?.reattempt_enabled
        : !(
            assessmentDetails?.is_attempted && !(isDeclaredFail && isReattempt)
          ))
        ? parseInt(submitted_answer, 10)
        : 0;

    return (
      <div key={id} className="assessment-question">
        <div style={{ display: "flex", gap: '5px' }}>
          <p className="question-text" style={{ flexGrow: 1 }}>
            {index + 1}. {question.question}
          </p>
          <p style={{whiteSpace: 'nowrap'}}>
            {question.total_marks} Mark{question.total_marks > 1 ? "s" : ""}
          </p>
        </div>

        <Form.Item
          name={`question-${id}`}
          rules={[{ required: true, message: "Please select an answer" }]}
          style={{ marginBottom: "20px" }}
        >
          {is_multiple_choice_question ? (
            <Radio.Group
              defaultValue={submittedAnswerNum}
              disabled={isDisabled}
            >
              {options.map((option, idx) => {
                const answerStyle = getAnswerStyleInfo(
                  is_correct,
                  idx,
                  submittedAnswerNum
                );

                return (
                  <div key={idx} style={{ marginBottom: "10px" }}>
                    <Radio
                      className={answerStyle?.className || ""}
                      value={idx + 1}
                    >
                      {option.option} {answerStyle?.text || ""}
                    </Radio>
                  </div>
                );
              })}
            </Radio.Group>
          ) : (
            <TextArea
              autoSize={{ minRows: 5 }}
              readOnly={isDisabled}
              placeholder="Your Answer"
              defaultValue={
                assessmentDetails?.is_attempted && !isReattempt
                  ? submitted_answer
                  : ""
              }
            />
          )}
        </Form.Item>

        {typeof is_correct !== "undefined" && is_multiple_choice_question && !isReattempt && (
          <div className="answer-feedback">
            {is_correct ? (
              <Text type="success">
                <CheckOutlined /> Correct!
              </Text>
            ) : (
              <Text type="danger">
                <CloseOutlined /> Wrong!
              </Text>
            )}
          </div>
        )}

        {typeof is_correct !== "undefined" &&
          !is_correct &&
          answerIndex >= 0 &&
          options[answerIndex] && (
            <div className="correct-answer-text">
              *Correct answer is <span>"{options[answerIndex].option}"</span>.
            </div>
          )}
      </div>
    );
  };

  const renderSubmitSection = () => {
    if (!assessmentDetails) return null;

    if (assessmentDetails.is_submitted) {
      return (
        <div className="submission-confirmation">
          <Form.Item style={{ marginBottom: "5px" }}>
            <Button
              style={{ background: "#389E0D", color: "#fff" }}
              icon={<CheckCircleOutlined />}
              disabled
            >
              Successfully Submitted
            </Button>
          </Form.Item>
          <Text italic>
            Thank you for your response! Your answers have been recorded. Please
            check back later for updates.
          </Text>
        </div>
      );
    }

    if (!isDisabled) {
      return (
        <div className="submit-section">
          <Form.Item>
            <Button type="primary" loading={isProcessing} htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </div>
      );
    }

    return null;
  };

  // Loading and error states
  if (isLoading) {
    return <Loader style={{ marginTop: "20%", justifyContent: "center" }} />;
  }
  if (errorMessage || !assessmentDetails) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2>Error</h2>
        <p>{errorMessage || "Assessment details not available"}</p>
        <Button onClick={() => navigate("/lms")} type="primary">
          Return LMS
        </Button>
      </div>
    );
  }
  const { is_attempted, total_marks, obtained_marks, percentage } =
    assessmentDetails;
  return (
    <Layout>
      <Layout className="max-width">
        <Content>
          <PageHeading
            onBackClick={() => navigate(backURL)}
            heading={assessmentDetails.name}
            rightContent={renderResultStatus()}
          />

          <LMSBreadcrumb courseName={assessmentDetails.name} />
          <Title level={4} style={{ margin: 0 }}>
            Review Assessment{" "}
            {is_attempted && total_marks && (
              <div>
                <Text italic type="secondary" style={{ color: "#1D322D" }}>
                  <span style={{ fontSize: "14px", fontWeight: 500 }}>
                    Marks Obtained: {obtained_marks}/{total_marks}
                  </span>{" "}
                </Text>
              </div>
            )}
          </Title>
          <div className="assessment-form-container">
            <Form form={form} onFinish={handleSubmit} className="quiz-question">
              {questions.map((question: any, index: number) =>
                renderQuestionOptions(question, index)
              )}
              {renderSubmitSection()}
            </Form>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AssessmentPage;
